/**
 * Subscription Context — Provides subscription state to entire app
 * Follows AuthContext pattern: createContext, Provider, custom hook with error check
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getSubscriptionPlans, checkSubscription } from '../services/subscriptionService';

const SubscriptionContext = createContext();

export const SubscriptionProvider = ({ children }) => {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [features, setFeatures] = useState([]);
  const [currentPlan, setCurrentPlan] = useState('free');
  const [loading, setLoading] = useState(true);

  // Load plans on mount (public, no auth needed)
  useEffect(() => {
    const loadPlans = async () => {
      const result = await getSubscriptionPlans();
      if (result.success) {
        setPlans(result.data);
      }
    };
    loadPlans();
  }, []);

  // Check subscription when user changes
  useEffect(() => {
    const loadSubscription = async () => {
      if (!user) {
        // Reset to free on sign-out
        setSubscription(null);
        setFeatures([]);
        setCurrentPlan('free');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const result = await checkSubscription();
        if (result.success && result.data) {
          setSubscription(result.data);
          setFeatures(result.data.features || []);
          setCurrentPlan(result.data.currentPlan || 'free');
        }
      } catch (err) {
        console.error('Error loading subscription:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSubscription();
  }, [user]);

  // Check if user has a specific feature
  const hasFeature = useCallback(
    (featureCode) => {
      return features.includes(featureCode);
    },
    [features]
  );

  // Refresh subscription (call after purchase)
  const refreshSubscription = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const result = await checkSubscription();
      if (result.success && result.data) {
        setSubscription(result.data);
        setFeatures(result.data.features || []);
        setCurrentPlan(result.data.currentPlan || 'free');
      }
    } catch (err) {
      console.error('Error refreshing subscription:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const value = {
    plans,
    subscription,
    features,
    currentPlan,
    loading,
    hasFeature,
    refreshSubscription,
    isPro: ['pro_yearly', 'bundle_yearly', 'total_freedom'].includes(currentPlan),
    isFree: currentPlan === 'free',
  };

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};
